import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import Button from '../../components/Button';
import { axiosKK } from '../../services/networkRequest';
import { downloadCsv } from '../../services/downloadBlob';
import errorMessage from '../../services/errorMessage';
import { fetchAdminPartners } from '../../services/adminPartners';
import {
  InvoiceAdditionalFeeDTO,
  InvoiceWeekDTO,
} from '../../models/APIModels';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import {
  AdminNotice,
  AdminPageShell,
  DataTable,
  DataTableColumn,
  EmptyState,
  FilterBar,
  TabBar,
} from '../../components/admin/Shared';

type Scope = 'partner' | 'warehouse';

// Warehouse invoices have a different shape — a single line-item list per month.
interface WarehouseInvoiceItem {
  label: string;
  quantity?: number;
  rate?: number;
  amount?: number;
  [k: string]: unknown;
}
interface WarehouseInvoiceResponse {
  month?: string;
  items: WarehouseInvoiceItem[];
  subtotal?: number;
  vat?: number;
  total?: number;
  isLocked?: boolean;
}
interface PartnerInvoicesResponse {
  month?: string;
  weeks: InvoiceWeekDTO[];
  courierName?: string;
  subtotal?: number;
  vat?: number;
  total?: number;
  isLocked?: boolean;
}

interface InvoiceLineRow {
  key: string;
  line: React.ReactNode;
  qty: React.ReactNode;
  rate: React.ReactNode;
  amount: React.ReactNode;
}

interface SummaryRow {
  key: string;
  label: React.ReactNode;
  amount: React.ReactNode;
}

function monthISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function pence(p?: number | null): string {
  if (p == null) return '—';
  return `£${(p / 100).toFixed(2)}`;
}

function readPartnerIdFromSearch(search: string): number | null {
  const raw = new URLSearchParams(search).get('partnerId');
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const lineColumns: DataTableColumn<InvoiceLineRow>[] = [
  { key: 'line', label: 'Line', render: (r) => r.line },
  { key: 'qty', label: 'Qty / Count', render: (r) => r.qty },
  { key: 'rate', label: 'Rate', render: (r) => r.rate },
  { key: 'amount', label: 'Amount', render: (r) => r.amount },
];

const warehouseLineColumns: DataTableColumn<InvoiceLineRow>[] = [
  { key: 'line', label: 'Line', render: (r) => r.line },
  { key: 'qty', label: 'Qty', render: (r) => r.qty },
  { key: 'rate', label: 'Rate', render: (r) => r.rate },
  { key: 'amount', label: 'Amount', render: (r) => r.amount },
];

const summaryColumns: DataTableColumn<SummaryRow>[] = [
  { key: 'label', label: '', render: (r) => r.label },
  { key: 'amount', label: '', render: (r) => r.amount },
];

function buildSummaryRows(
  subtotal?: number,
  vat?: number,
  total?: number
): SummaryRow[] {
  return [
    { key: 'subtotal', label: 'Subtotal', amount: pence(subtotal) },
    { key: 'vat', label: 'VAT (20%)', amount: pence(vat) },
    {
      key: 'total',
      label: <strong>Total</strong>,
      amount: <strong>{pence(total)}</strong>,
    },
  ];
}

function buildBoxRow(
  key: string,
  label: string,
  rate?: { boxes: number; rate: number }
): InvoiceLineRow | null {
  if (!rate || rate.boxes === 0) return null;
  return {
    key,
    line: label,
    qty: rate.boxes,
    rate: pence(rate.rate),
    amount: pence(rate.boxes * rate.rate),
  };
}

function buildPartnerWeekRows(w: InvoiceWeekDTO): InvoiceLineRow[] {
  const rows: InvoiceLineRow[] = [];
  const doorstepCollections = buildBoxRow('doorstep-coll', 'Doorstep Collections', w.doorstepCollections);
  const timeslotCollections = buildBoxRow('timeslot-coll', 'Timeslot Collections', w.timeslotCollections);
  const doorstepReturns = buildBoxRow('doorstep-ret', 'Doorstep Returns', w.doorstepReturns);
  const timeslotReturns = buildBoxRow('timeslot-ret', 'Timeslot Returns', w.timeslotReturns);
  if (doorstepCollections) rows.push(doorstepCollections);
  if (timeslotCollections) rows.push(timeslotCollections);
  if (doorstepReturns) rows.push(doorstepReturns);
  if (timeslotReturns) rows.push(timeslotReturns);

  if (w.minCharge > 0) {
    rows.push({
      key: 'min-charge',
      line: 'Minimum Charge',
      qty: '—',
      rate: '—',
      amount: pence(w.minCharge),
    });
  }
  if (w.failedDeliveries && w.failedDeliveries.count > 0) {
    rows.push({
      key: 'failed-deliveries',
      line: 'Failed Deliveries',
      qty: w.failedDeliveries.count,
      rate: pence(w.failedDeliveries.rate),
      amount: pence(w.failedDeliveries.count * w.failedDeliveries.rate),
    });
  }
  (w.additionalFees || [])
    .filter((f: InvoiceAdditionalFeeDTO) => f.quantity > 0)
    .forEach((f: InvoiceAdditionalFeeDTO, i: number) => {
      rows.push({
        key: `fee-${i}`,
        line: `${f.label}${f.applyMode ? ` (${f.applyMode})` : ''}`,
        qty: f.quantity,
        rate: pence(f.rate),
        amount: pence(f.quantity * f.rate),
      });
    });
  return rows;
}

function InvoicesPage({ history, location }: RouteComponentProps) {
  const [scope, setScope] = useState<Scope>('partner');
  const [month, setMonth] = useState<string>(monthISO);
  const partnerId = readPartnerIdFromSearch(location.search);

  const [partnerOptions, setPartnerOptions] = useState<{ id: number; name: string }[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(true);

  const [partnerData, setPartnerData] = useState<PartnerInvoicesResponse | null>(null);
  const [warehouseData, setWarehouseData] = useState<WarehouseInvoiceResponse | null>(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [netError, setNetError] = useState<string>();
  const [banner, setBanner] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    // The partners table, not partner users — a partner with orders but no
    // users yet could not be invoiced when this was derived from users.
    fetchAdminPartners()
      .then((partners) => {
        if (cancelled) return;
        const opts = partners.map((p) => ({ id: p.id, name: p.name }));
        setPartnerOptions(opts);
        if (opts.length === 1 && partnerId == null) {
          history.replace({
            pathname: location.pathname,
            search: `?partnerId=${opts[0].id}`,
          });
        }
      })
      .catch(() => {
        // Silent — picker stays empty.
      })
      .finally(() => {
        if (!cancelled) setPartnersLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPartnerId = (next: number | null) => {
    history.push({
      pathname: location.pathname,
      search: next != null ? `?partnerId=${next}` : '',
    });
  };

  useEffect(() => {
    if (partnerId == null) {
      setPartnerData(null);
      setWarehouseData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);
    setBanner(undefined);

    const url =
      scope === 'partner'
        ? '/logistics/dashboard/partner/invoices'
        : '/logistics/dashboard/warehouse/invoices';

    axiosKK
      .get(url, { params: { month, partnerId } })
      .then(({ data }) => {
        if (cancelled) return;
        if (scope === 'partner') setPartnerData(data as PartnerInvoicesResponse);
        else setWarehouseData(data as WarehouseInvoiceResponse);
      })
      .catch((err) => {
        if (!cancelled) errorMessage(setNetError)(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scope, month, partnerId]);

  const exportCsv = () => {
    if (partnerId == null) return;
    const url =
      scope === 'partner'
        ? '/logistics/dashboard/partner/invoices'
        : '/logistics/dashboard/warehouse/invoices';
    downloadCsv(url, `${scope}-invoices-${month}.csv`, { month, partnerId }).catch((err) =>
      errorMessage(setNetError)(err)
    );
  };

  const lockWarehouseMonth = () => {
    if (partnerId == null) return;
    setBusy(true);
    axiosKK
      .post(`/logistics/dashboard/warehouse/invoices/${month}/lock`, undefined, {
        params: { partnerId },
      })
      .then(() => setBanner(`Locked warehouse invoices for ${month}.`))
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setBusy(false));
  };

  return (
    <AdminPageShell
      title="Invoices"
      onBack={() => history.push('/admin/dashboard')}
      contextBar={
        <div className="admin-partner-picker">
          <label
            className="admin-partner-picker__label"
            htmlFor="invoices-partner-picker"
          >
            Partner
          </label>
          <select
            id="invoices-partner-picker"
            className="admin-partner-picker__select admin-select"
            value={partnerId ?? ''}
            onChange={(e) =>
              setPartnerId(e.target.value ? Number(e.target.value) : null)
            }
            disabled={partnersLoading || partnerOptions.length === 0}
          >
            <option value="">
              {partnersLoading
                ? 'Loading…'
                : partnerOptions.length === 0
                  ? 'No partners available'
                  : 'Select a partner…'}
            </option>
            {partnerOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      }
      notice={
        netError ? (
          <AdminNotice intent="error">{netError}</AdminNotice>
        ) : banner ? (
          <AdminNotice intent="success">{banner}</AdminNotice>
        ) : undefined
      }
      tabs={
        <TabBar<Scope>
          items={[
            { id: 'partner', label: 'Partner' },
            { id: 'warehouse', label: 'Warehouse' },
          ]}
          activeId={scope}
          onChange={setScope}
          ariaLabel="Invoice scope"
        />
      }
    >
      <section className="admin-partner-pricing">
        <FilterBar
          actions={
            <>
              <Button
                variant="secondary"
                size="md"
                leftIcon={<ArrowDownTrayIcon />}
                onClick={exportCsv}
              >
                Export CSV
              </Button>
              {scope === 'warehouse' && (
                <Button
                  variant="danger"
                  size="md"
                  loading={busy}
                  onClick={lockWarehouseMonth}
                >
                  Lock month
                </Button>
              )}
            </>
          }
        >
          <FilterBar.Item label="Month" htmlFor="invoices-month">
            <input
              id="invoices-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </FilterBar.Item>
        </FilterBar>

        {partnerId == null ? (
          <EmptyState
            variant="select-prerequisite"
            title="Select a partner"
            body="Select a partner from the picker above to load invoices."
          />
        ) : loading ? (
          <EmptyState title="Loading invoices…" />
        ) : scope === 'partner' ? (
          <PartnerInvoices data={partnerData} />
        ) : (
          <WarehouseInvoices data={warehouseData} />
        )}
      </section>
    </AdminPageShell>
  );
}

function PartnerInvoices({ data }: { data: PartnerInvoicesResponse | null }) {
  if (!data || !data.weeks || data.weeks.length === 0) {
    return <p>No invoice data for this month.</p>;
  }
  return (
    <>
      {data.weeks.map((w) => (
        <div key={w.weekCommencing} className="pricing-section">
          <h4>{w.displayLabel}</h4>
          <DataTable<InvoiceLineRow>
            columns={lineColumns}
            rows={buildPartnerWeekRows(w)}
            keyOf={(r) => r.key}
            emptyMessage="No charges this week."
          />
        </div>
      ))}

      {(data.subtotal != null || data.vat != null || data.total != null) && (
        <div className="pricing-section">
          <DataTable<SummaryRow>
            columns={summaryColumns}
            rows={buildSummaryRows(data.subtotal, data.vat, data.total)}
            keyOf={(r) => r.key}
          />
        </div>
      )}
    </>
  );
}

function WarehouseInvoices({ data }: { data: WarehouseInvoiceResponse | null }) {
  if (!data || !data.items || data.items.length === 0) {
    return <p>No invoice data for this month.</p>;
  }
  const itemRows: InvoiceLineRow[] = data.items
    .filter((it) => !(it.quantity === 0 && (it.amount || 0) === 0))
    .map((it, i) => ({
      key: `item-${i}`,
      line: it.label,
      qty: it.quantity ?? '—',
      rate: pence(it.rate),
      amount: pence(it.amount),
    }));
  return (
    <div className="pricing-section">
      <h4>{data.month || ''}</h4>
      <DataTable<InvoiceLineRow>
        columns={warehouseLineColumns}
        rows={itemRows}
        keyOf={(r) => r.key}
        emptyMessage="No items this month."
      />
      {(data.subtotal != null || data.vat != null || data.total != null) && (
        <DataTable<SummaryRow>
          columns={summaryColumns}
          rows={buildSummaryRows(data.subtotal, data.vat, data.total)}
          keyOf={(r) => r.key}
        />
      )}
      {data.isLocked && <p className="pricing-hint">Month is locked.</p>}
    </div>
  );
}

export default withRouter(InvoicesPage);
